<!-- FT-216 REQ-01, REQ-03, REQ-12, REQ-15: блок «Динамическое ценообразование» на вкладке «Цены» настроек компании.
     Свёрнутая панель внизу вкладки, своё сохранение (кнопка «Сохранить» у настроек, прочие действия сохраняются
     сразу), вне anyDirty/saveAll. Сверху вниз: настройки (включение, режим «если нет ответа», шаг, границы,
     направления, заглушённые группы), «Применено за 7 дней» с откатом, ручные поправки на даты.
     Без услуги в плане: пояснение о тарифе; полей настроек, включения и создания нет; если было включено —
     переключатель только на выключение (PATCH { enabled: false }); «Применено за 7 дней» с откатом, список и
     отмена поправок доступны (REQ-15, CTR-10). -->
<template>
  <section v-if="visible" class="mb-10" data-testid="dynamic-pricing-panel">
    <v-divider class="mb-4" />
    <v-expansion-panels v-model="open" flat class="dp-panels">
      <v-expansion-panel>
        <v-expansion-panel-header data-testid="dp-header">
          <span>
            <v-icon small class="mr-1">mdi-chart-line-variant</v-icon>{{ $t("dynamic_pricing.title") }}<BetaChip />
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-alert v-if="!inPlan" type="info" dense text outlined class="mb-3" data-testid="dp-not-in-plan">
            {{ $t("dynamic_pricing.not_in_plan") }}
          </v-alert>

          <!-- Настройки (REQ-01, CTR-01) -->
          <v-skeleton-loader v-if="settingsLoading" type="list-item" />
          <div v-else-if="settingsFailed" class="text-body-2 mb-2" data-testid="dp-settings-failed">
            {{ $t("dynamic_pricing.settings_load_error") }}
            <v-btn x-small text color="primary" class="ml-1" @click="loadSettings">{{ $t("custom.retry") }}</v-btn>
          </div>
          <template v-else-if="settings">
            <!-- Без услуги включить нельзя; включённое можно только выключить — иначе при возврате услуги
                 автоприменение возобновилось бы без участия админа (REQ-15). Выключение уходит сразу. -->
            <div v-if="!inPlan && settings.enabled" class="mb-3" data-testid="dp-enabled-off-only">
              <v-switch :key="offOnlyKey" :input-value="true" inset dense hide-details class="mt-0"
                :label="$t('dynamic_pricing.enabled_label')" :disabled="saving" data-testid="dp-enabled-off-only-switch"
                @change="onOffOnlyChange" />
              <div class="text-caption text--secondary">{{ $t("dynamic_pricing.off_only_hint") }}</div>
            </div>

            <div v-if="inPlan" class="mb-4" data-testid="dp-settings-form">
              <v-switch v-model="form.enabled" inset dense hide-details class="mt-0 mb-2"
                :label="$t('dynamic_pricing.enabled_label')" data-testid="dp-enabled" />

              <div v-if="!settings.groups_present" class="d-flex align-center flex-wrap mb-2" style="gap: 8px"
                data-testid="dp-no-groups">
                <span class="text-caption text--secondary">{{ $t("price_groups.not_built") }}</span>
                <v-btn x-small color="primary" :loading="building" data-testid="dp-build-groups" @click="buildGroups">
                  {{ $t("price_groups.build") }}
                </v-btn>
              </div>

              <div class="text-subtitle-2 mt-2">{{ $t("dynamic_pricing.no_response_label") }}</div>
              <v-radio-group v-model="form.no_response_mode" dense hide-details class="mt-1" data-testid="dp-mode">
                <v-radio v-for="mode in modeOptions" :key="mode.value" :value="mode.value" :data-testid="`dp-mode-${mode.value}`">
                  <template #label><span class="text-body-2" :data-testid="`dp-mode-label-${mode.value}`">{{ mode.text }}</span></template>
                </v-radio>
              </v-radio-group>

              <v-row dense class="mt-2">
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.no_response_days" type="number" dense outlined
                    :label="$t('dynamic_pricing.no_response_days')" :rules="[daysRule]" data-testid="dp-days" />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-select v-model="form.step_percent" :items="stepOptions" dense outlined
                    :label="$t('dynamic_pricing.step_label')" data-testid="dp-step" />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.floor_percent" type="number" dense outlined
                    :label="$t('dynamic_pricing.floor_label')" :hint="$t('dynamic_pricing.floor_hint')" persistent-hint
                    :rules="[floorRule]" data-testid="dp-floor" />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field v-model.number="form.ceiling_percent" type="number" dense outlined
                    :label="$t('dynamic_pricing.ceiling_label')" :hint="$t('dynamic_pricing.ceiling_hint')" persistent-hint
                    :rules="[ceilingRule]" data-testid="dp-ceiling" />
                </v-col>
              </v-row>
              <v-checkbox v-model="form.suggest_down" dense hide-details class="mt-1"
                :label="$t('dynamic_pricing.suggest_down')" data-testid="dp-suggest-down" />
              <v-checkbox v-model="form.suggest_up" dense hide-details class="mt-1"
                :label="$t('dynamic_pricing.suggest_up')" data-testid="dp-suggest-up" />

              <div v-if="settings.suppressed_group_ids.length" class="mt-3" data-testid="dp-suppressed">
                <div class="text-subtitle-2">{{ $t("dynamic_pricing.suppressed_title") }}</div>
                <div v-for="id in settings.suppressed_group_ids" :key="id" class="d-flex align-center"
                  :data-testid="`dp-suppressed-${id}`">
                  <span class="text-body-2">{{ suppressedLabel(id) }}</span>
                  <v-btn x-small text color="primary" class="ml-2" :disabled="saving" data-testid="dp-unsuppress"
                    @click="unsuppress(id)">{{ $t("dynamic_pricing.unsuppress") }}</v-btn>
                </div>
              </div>

              <v-btn small color="primary" class="mt-3" :disabled="!canSave" :loading="saving" data-testid="dp-save"
                @click="save">{{ $t("custom.save") }}</v-btn>
            </div>
          </template>

          <!-- «Применено за 7 дней» с откатом: при любом «включено» и без услуги (REQ-08, REQ-15) -->
          <div class="mb-4">
            <v-skeleton-loader v-if="recommendationsLoading" type="list-item" />
            <!-- Не загрузилось — так и говорим, с повтором: «ничего не применено» спрятало бы доступный откат -->
            <div v-else-if="recommendationsFailed" data-testid="dp-applied-failed">
              <div class="text-subtitle-2 mb-1">{{ $t("dynamic_pricing.applied_title") }}</div>
              <span class="text-body-2">{{ $t("dynamic_pricing.applied_load_error") }}</span>
              <v-btn x-small text color="primary" class="ml-1" data-testid="dp-applied-retry" @click="loadRecommendations">
                {{ $t("custom.retry") }}
              </v-btn>
            </div>
            <applied-recommendations v-else :items="applied" :unit="recommendationsUnit" @changed="onAppliedChanged" />
          </div>

          <h4 class="mb-1">{{ $t("dynamic_pricing.adjustments_title") }}</h4>
          <div class="text-caption text--secondary mb-2">{{ $t("dynamic_pricing.adjustments_hint") }}</div>

          <v-alert v-if="boundsWarning" type="warning" dense text dismissible class="mb-3"
            data-testid="dp-bounds-warning" @input="boundsWarning = false">
            {{ $t("dynamic_pricing.warning_out_of_car_bounds") }}
          </v-alert>

          <v-skeleton-loader v-if="loading" type="list-item, list-item" />
          <div v-else-if="loadFailed" class="text-body-2 mb-2" data-testid="dp-load-failed">
            {{ $t("dynamic_pricing.load_error") }}
            <v-btn x-small text color="primary" class="ml-1" data-testid="dp-retry" @click="load">{{ $t("custom.retry") }}</v-btn>
          </div>
          <div v-else-if="!adjustments.length" class="text-body-2 text--secondary mb-2" data-testid="dp-empty">
            {{ $t("dynamic_pricing.adjustments_empty") }}
          </div>
          <!-- Телефон: строка таблицы (даты, процент, область, источник, «Отменить») не помещается в 375 px —
               двухстрочные пункты списка, «Отменить» в v-list-item-action всегда виден. Desktop — таблица. -->
          <v-list v-else-if="isPhone" dense class="mb-2 py-0" data-testid="dp-list">
            <template v-for="(a, i) in adjustments">
              <v-divider v-if="i > 0" :key="`dp-divider-${a.id}`" />
              <v-list-item :key="a.id" class="px-0" :data-testid="`dp-row-${a.id}`">
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    {{ periodText(a) }}
                    <span class="font-weight-medium ml-1 text-no-wrap" data-testid="dp-row-percent">{{ percentText(a) }}</span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">{{ scopeText(a) }} · {{ sourceText(a) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <v-btn x-small text color="error" :disabled="!!revoking" data-testid="dp-revoke"
                    @click="askRevoke(a)">{{ $t("dynamic_pricing.revoke") }}</v-btn>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
          <v-simple-table v-else dense class="mb-2" data-testid="dp-list">
            <tbody>
              <tr v-for="a in adjustments" :key="a.id" :data-testid="`dp-row-${a.id}`">
                <td class="px-2 text-no-wrap">{{ periodText(a) }}</td>
                <td class="px-2 text-no-wrap font-weight-medium" data-testid="dp-row-percent">{{ percentText(a) }}</td>
                <td class="px-2">{{ scopeText(a) }}</td>
                <td class="px-2 text--secondary">{{ sourceText(a) }}</td>
                <td class="px-2 text-right">
                  <v-btn x-small text color="error" :disabled="!!revoking" data-testid="dp-revoke"
                    @click="askRevoke(a)">{{ $t("dynamic_pricing.revoke") }}</v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-btn v-if="inPlan" small outlined color="primary" data-testid="dp-add" @click="dialog = true">
            <v-icon left small>mdi-plus</v-icon>{{ $t("dynamic_pricing.add") }}
          </v-btn>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <date-adjustment-dialog v-if="inPlan" v-model="dialog" @created="onCreated" />

    <v-dialog v-model="revokeDialog" max-width="440" :persistent="!!revoking">
      <v-card data-testid="dp-revoke-dialog">
        <v-card-title class="text-h6">{{ $t("dynamic_pricing.revoke_title") }}</v-card-title>
        <v-card-text>
          <div v-if="revokeTarget" class="font-weight-medium mb-2" data-testid="dp-revoke-target">
            {{ revokeTargetText }}
          </div>
          <div data-testid="dp-revoke-warning">{{ $t("dynamic_pricing.revoke_warning") }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="!!revoking" data-testid="dp-revoke-cancel" @click="revokeDialog = false">
            {{ $t("custom.cancel") }}
          </v-btn>
          <v-btn color="error" :loading="!!revoking" data-testid="dp-revoke-confirm" @click="confirmRevoke">
            {{ $t("dynamic_pricing.revoke_confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import DateAdjustmentDialog, { formatAdjustmentPeriod } from "./DateAdjustmentDialog.vue";
import AppliedRecommendations from "@/components/dashboard/dynamic_pricing/AppliedRecommendations.vue";
import {
  CEILING_PERCENT_RANGE,
  dynamicPricingApi,
  dynamicPricingInPlan,
  FLOOR_PERCENT_RANGE,
  NO_RESPONSE_DAYS_RANGE,
  parseDynamicPricingError,
  RECOMMENDATION_KINDS,
  reportDynamicPricingError,
  reportDynamicPricingLoadError,
  SETTINGS_FORM_FIELDS,
  STEP_PERCENTS,
} from "@/api/dynamicPricing";
import { formatAdjustmentPercent } from "@/utils/adjustmentPercent";
import { fetchPriceGroups, rebuildPriceGroups } from "@/utils/priceGroupsApi";
import { priceGroupLabel, priceGroupLabelOptions } from "@/utils/priceGroupLabel";

// Сколько групп или кодов объектов показать в области поправки; остальные — «и ещё N».
const SCOPE_LABELS_SHOWN = 3;

function intIn(value, [min, max]) {
  return Number.isInteger(value) && value >= min && value <= max;
}

function pickForm(settings) {
  return SETTINGS_FORM_FIELDS.reduce((acc, key) => Object.assign(acc, { [key]: settings[key] }), {});
}

// Только superadmin/admin (REQ-13). Список грузится при первом раскрытии панели (свёрнутая — запросов нет).
export default {
  name: "DynamicPricingPanel",
  components: { DateAdjustmentDialog, AppliedRecommendations },
  data() {
    return {
      open: undefined, // индекс раскрытой панели; undefined — свёрнута
      loaded: false,
      loading: false,
      loadFailed: false,
      adjustments: [],
      dialog: false,
      boundsWarning: false,
      revokeDialog: false,
      revokeTarget: null,
      revoking: null, // id отменяемой поправки
      // настройки (CTR-01)
      settings: null, // последний ответ сервера (normalizeSettings)
      form: {}, // правка формы до «Сохранить»
      settingsLoading: false,
      settingsFailed: false,
      saving: false,
      offOnlyKey: 0, // пересоздать переключатель «только выключить», если выключение не прошло
      building: false,
      groups: [], // ценовые группы — подписи заглушённых
      groupsUnit: null,
      // «Применено за 7 дней» (CTR-04)
      applied: [],
      recommendationsUnit: null,
      recommendationsLoading: false,
      recommendationsFailed: false,
    };
  },
  computed: {
    visible() {
      return !!this.isAdminPlus;
    },
    isPhone() {
      return !!(this.$vuetify && this.$vuetify.breakpoint.xsOnly);
    },
    // «период, процент» без висящей запятой, если процента нет
    revokeTargetText() {
      const a = this.revokeTarget;
      return a ? [this.periodText(a), this.percentText(a)].filter(Boolean).join(", ") : "";
    },
    // REQ-15: только ключ dynamic_pricing в плане, не общий includedInPlan.
    inPlan() {
      return dynamicPricingInPlan(this.$store && this.$store.getters.getMainCompany);
    },
    // REQ-01: подпись режима auto говорит прямо, что поправки на ближайшие дни применяются сразу.
    modeOptions() {
      const n = this.form.no_response_days;
      return [
        { value: "show", text: this.$t("dynamic_pricing.mode_show") },
        { value: "remind", text: this.$t("dynamic_pricing.mode_remind", { n }) },
        { value: "auto", text: this.$t("dynamic_pricing.mode_auto", { n }) },
      ];
    },
    stepOptions() {
      return STEP_PERCENTS.map((value) => ({ value, text: `${value} %` }));
    },
    formValid() {
      return this.daysRule(this.form.no_response_days) === true
        && this.floorRule(this.form.floor_percent) === true
        && this.ceilingRule(this.form.ceiling_percent) === true;
    },
    dirty() {
      if (!this.settings) return false;
      return SETTINGS_FORM_FIELDS.some((key) => this.form[key] !== this.settings[key]);
    },
    canSave() {
      return this.inPlan && this.dirty && this.formValid && !this.saving;
    },
  },
  watch: {
    open(index) {
      if (index === 0 && !this.loaded) {
        this.load();
        this.loadSettings();
        this.loadRecommendations();
      }
    },
  },
  methods: {
    daysRule(value) {
      return intIn(value, NO_RESPONSE_DAYS_RANGE) || this.$t("dynamic_pricing.days_invalid");
    },
    floorRule(value) {
      return intIn(value, FLOOR_PERCENT_RANGE) || this.$t("dynamic_pricing.floor_invalid");
    },
    ceilingRule(value) {
      return intIn(value, CEILING_PERCENT_RANGE) || this.$t("dynamic_pricing.ceiling_invalid");
    },
    // keepForm — действие вне формы (вернуть группу): несохранённые правки полей не теряем.
    applySettings(settings, { keepForm = false } = {}) {
      this.settings = settings;
      if (!keepForm) this.form = pickForm(settings);
      if (settings.suppressed_group_ids.length && !this.groups.length) this.loadGroups();
    },
    async loadSettings() {
      this.settingsLoading = true;
      this.settingsFailed = false;
      try {
        this.applySettings(await dynamicPricingApi.getSettings(this.axios));
      } catch (error) {
        this.settingsFailed = true;
        reportDynamicPricingLoadError(this, error, this.$t("dynamic_pricing.settings_load_error"));
      } finally {
        this.settingsLoading = false;
      }
    },
    async loadRecommendations() {
      this.recommendationsLoading = !this.applied.length;
      try {
        const result = await dynamicPricingApi.listRecommendations(this.axios);
        this.applied = result.applied || [];
        this.recommendationsUnit = result.unit || null;
        this.recommendationsFailed = false;
      } catch (error) {
        this.recommendationsFailed = true;
        reportDynamicPricingLoadError(this, error, this.$t("dynamic_pricing.applied_load_error"));
      } finally {
        this.recommendationsLoading = false;
      }
    },
    // Откат перечитывает «Применено за 7 дней»; откат поправки на даты отменяет на сервере и её поправки —
    // перечитываем и их список, иначе отменённые оставались бы в нём действующими.
    onAppliedChanged(rec) {
      this.loadRecommendations();
      if (rec && rec.kind === RECOMMENDATION_KINDS.DATE_ADJUSTMENT) this.load();
    },
    // Перечитать настройки, не трогая несохранённую правку формы (после «Собрать группы» меняется groups_present).
    async refreshSettings() {
      try {
        this.applySettings(await dynamicPricingApi.getSettings(this.axios), { keepForm: true });
      } catch (error) {
        reportDynamicPricingLoadError(this, error, this.$t("dynamic_pricing.settings_load_error"));
      }
    },
    // Подписи заглушённых групп — из списка групп FT-217; группы уже нет (архив) — «Группа #id».
    async loadGroups() {
      try {
        const data = await fetchPriceGroups(this);
        this.groups = Array.isArray(data.groups) ? data.groups : [];
        this.groupsUnit = data.unit || null;
      } catch (error) {
        this.groups = [];
      }
    },
    // Первые SCOPE_LABELS_SHOWN подписей через запятую, остальное — «и ещё N».
    labelList(labels) {
      const shown = labels.slice(0, SCOPE_LABELS_SHOWN).join(", ");
      const rest = labels.length - SCOPE_LABELS_SHOWN;
      return rest > 0 ? `${shown} ${this.$t("dynamic_pricing.scope_more", { n: rest })}` : shown;
    },
    suppressedLabel(id) {
      const group = this.groups.find((g) => g.id === id);
      return group
        ? priceGroupLabel(group, priceGroupLabelOptions(this, this.groupsUnit))
        : this.$t("dynamic_pricing.rec.group_n", { id });
    },
    async patchSettings(attrs, { successKey, keepForm = false } = {}) {
      this.saving = true;
      try {
        this.applySettings(await dynamicPricingApi.updateSettings(this.axios, attrs), { keepForm });
        if (successKey) this.$notify && this.$notify(this.$t(successKey), "", "success");
        return true;
      } catch (error) {
        reportDynamicPricingError(this, error, this.$t("dynamic_pricing.settings_save_error"));
        return false;
      } finally {
        this.saving = false;
      }
    },
    save() {
      if (!this.canSave) return null;
      return this.patchSettings(pickForm(this.form), { successKey: "dynamic_pricing.settings_saved" });
    },
    // REQ-15: без услуги переключатель только выключает. Любое другое значение — не запрос.
    async onOffOnlyChange(value) {
      if (value !== false || this.saving) return;
      const ok = await this.patchSettings({ enabled: false }, { successKey: "dynamic_pricing.disabled" });
      if (!ok) this.offOnlyKey += 1; // вернуть переключатель во «вкл.»: выключение не прошло
    },
    unsuppress(id) {
      const rest = this.settings.suppressed_group_ids.filter((g) => g !== id);
      return this.patchSettings({ suppressed_group_ids: rest }, { successKey: "dynamic_pricing.unsuppressed", keepForm: true });
    },
    async buildGroups() {
      if (this.building) return;
      this.building = true;
      try {
        await rebuildPriceGroups(this, { dryRun: false });
        this.$notify && this.$notify(this.$t("price_groups.built"), "", "success");
        await this.refreshSettings();
      } catch (error) {
        this.setError && this.setError(error, this.$t("price_groups.build_error"));
      } finally {
        this.building = false;
      }
    },
    async load() {
      this.loading = true;
      this.loadFailed = false;
      try {
        this.adjustments = await dynamicPricingApi.listAdjustments(this.axios);
        this.loaded = true;
        // Подписи области «Ценовые группы» — из списка групп FT-217 (тот же запрос, что у заглушённых).
        if (!this.groups.length && this.adjustments.some((a) => a.scope === "groups")) this.loadGroups();
      } catch (error) {
        this.loadFailed = true;
        // Ожидаемые отказы (роль) — без Rollbar и тоста: блок сам объясняет, что не загрузилось.
        if (!parseDynamicPricingError(error)) this.setError && this.setError(error, this.$t("dynamic_pricing.load_error"), { silent: true });
      } finally {
        this.loading = false;
      }
    },
    periodText(a) {
      return formatAdjustmentPeriod(a.starts_on, a.ends_on);
    },
    percentText(a) {
      return formatAdjustmentPercent(a.percent);
    },
    // Область поправки словами: группы — их подписи (диапазон цен), выбранные объекты — коды (api `cars`).
    // Ответ api без `cars` (до выката подписей) — прежнее число объектов.
    scopeText(a) {
      if (a.scope === "groups") return this.labelList((a.price_group_ids || []).map((id) => this.suppressedLabel(id)));
      if (a.scope === "cars") {
        const cars = a.cars || [];
        if (!cars.length) return this.$t("dynamic_pricing.scope_cars_n", { n: (a.car_ids || []).length });
        return this.labelList(cars.map((c) => c.code || c.name || String(c.id)));
      }
      return this.$t("dynamic_pricing.scope_company");
    },
    sourceText(a) {
      return a.source === "recommendation"
        ? this.$t("dynamic_pricing.source_recommendation")
        : this.$t("dynamic_pricing.source_manual");
    },
    async onCreated(created) {
      this.$notify && this.$notify(this.$t("dynamic_pricing.created"), "", "success");
      this.boundsWarning = Array.isArray(created.warnings) && created.warnings.includes("out_of_car_bounds");
      await this.load();
    },
    // Отмена — только после подтверждения (REQ-03): запрос revoke уходит из confirmRevoke.
    askRevoke(adjustment) {
      this.revokeTarget = adjustment;
      this.revokeDialog = true;
    },
    async confirmRevoke() {
      const target = this.revokeTarget;
      if (!target || this.revoking) return;
      this.revoking = target.id;
      try {
        await dynamicPricingApi.revokeAdjustment(this.axios, target.id);
        this.$notify && this.$notify(this.$t("dynamic_pricing.revoked"), "", "success");
      } catch (error) {
        // 404 not_found (уже отменена или чужая), 409 car_lock_unavailable и прочие отказы из списка — текстом
        // без setError.
        const parsed = parseDynamicPricingError(error);
        if (parsed) {
          this.$notify && this.$notify(this.$t("dynamic_pricing.revoke_error"), this.$t(parsed.i18nKey), "warning", 6000);
        } else {
          this.setError && this.setError(error, this.$t("dynamic_pricing.revoke_error"));
        }
      } finally {
        // И при успехе, и при отказе: диалог закрываем, список перечитываем; кнопки заблокированы до конца загрузки.
        this.revokeDialog = false;
        await this.load();
        this.revoking = null;
      }
    },
  },
};
</script>

<style scoped>
/* Заголовок и содержимое панели — по левому краю вкладки, как у остальных блоков «Цен». */
.dp-panels ::v-deep .v-expansion-panel-header,
.dp-panels ::v-deep .v-expansion-panel-content__wrap {
  padding-left: 0;
  padding-right: 0;
}
</style>
